"use client";

import { useEffect, useState } from "react";
import HeroSection from "../components/root/Hero/HeroSection";
import TrustBand from "../components/root/Hero/TrustBand";
import { ScrollStory } from "../components/root/Story/ScrollStory";
import { ProfileSection } from "../components/root/Profile/ProfileSection";
import { TrustSection } from "../components/root/Trust/TrustSection";
import ComparisonSection from "../components/root/Comparison/ComparisonSection";
import PricingSection from "../components/root/Pricing/PricingSection";
import FAQSection from "../components/root/FAQ/FAQSection";
import FinalCTASection from "../components/root/CTA/FinalCTASection";
import ContactSection from "../components/root/Contact/ContactSection";
import { consumePendingNav, type Profile, type ProfileNavDetail } from "../components/Utils/profileNav";
import type { PlanPricingByKey } from "@/lib/pricing/catalog";

function getLayoutTop(el: HTMLElement): number {
  let top = 0;
  let node: HTMLElement | null = el;
  while (node) {
    top += node.offsetTop;
    node = node.offsetParent as HTMLElement | null;
  }
  return top;
}

/**
 * The homepage, with the plan prices the server read from the catalogue.
 *
 * Order follows the visitor's objections (see the sales argument): the promise and the
 * film, the three-slide tour (the vise → nothing to build → from meeting to action), the
 * « who are you? » fork (individual or organisation arguments), trust, the comparison with Notion, Trello and Todoist, then prices, the last call to action, the FAQ and, at the very end, the contact form.
 *
 * The prices are fetched in the page (a Server Component) and threaded through rather than
 * fetched here: the pricing section has to be in the HTML a crawler receives, and a client-side
 * fetch would leave it empty on first paint.
 */
export default function HomeClient({ pricing }: { pricing: PlanPricingByKey }) {
  const [scrollToId, setScrollToId] = useState<string | null>(null);
  const [profile, setProfile] = useState<Profile>("Individual");

  // Navbar / footer links dispatch a section to reach, and switch the profile tab when
  // the target is a profile argument (`keepProfile` for shared sections like pricing).
  useEffect(() => {
    const handler = (e: Event) => {
      const { scrollTo, profile: next, keepProfile } = (e as CustomEvent<Partial<ProfileNavDetail>>).detail;
      if (next && !keepProfile) setProfile(next);
      if (scrollTo) setScrollToId(scrollTo);
    };
    window.addEventListener("flots:set-profile", handler);
    return () => window.removeEventListener("flots:set-profile", handler);
  }, []);

  // Apply any section nav queued from another page before landing here.
  useEffect(() => {
    const pending = consumePendingNav();
    if (!pending) return;
    // Reads a one-off intent queued in sessionStorage by another page; must
    // run post-mount, there is no render-time equivalent.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (pending.profile && !pending.keepProfile) setProfile(pending.profile);
    if (pending.scrollTo) setScrollToId(pending.scrollTo);
  }, []);

  // Scroll to the requested section once it is rendered.
  useEffect(() => {
    if (!scrollToId) return;
    let timeoutId: ReturnType<typeof setTimeout>;
    let attempts = 0;

    const tryScroll = () => {
      const el = document.getElementById(scrollToId);
      if (el) {
        // offsetTop gives layout position, unaffected by CSS transforms
        const top = getLayoutTop(el);
        // Tour beats are centred (on desktop their anchor sits where the beat is active).
        const centred = /^story-/.test(scrollToId) ? top - window.innerHeight / 2 + el.offsetHeight / 2 : top - 120;
        window.scrollTo({ top: centred, behavior: "smooth" });
        setScrollToId(null);
      } else if (attempts++ < 20) {
        timeoutId = setTimeout(tryScroll, 100);
      }
    };

    timeoutId = setTimeout(tryScroll, 80);
    return () => clearTimeout(timeoutId);
  }, [scrollToId]);

  return (
    <>
      <HeroSection />
      <TrustBand />
      <ScrollStory />
      <ProfileSection profile={profile} onProfileChange={setProfile} />
      <TrustSection />
      <ComparisonSection pricing={pricing} />
      <PricingSection pricing={pricing} />
      <FinalCTASection />
      <FAQSection />
      <ContactSection />
    </>
  );
}
